import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import debounce from 'lodash.debounce'
import { BasicSlider } from '../BasicSlider/BasicSlider'

const TabsContext = React.createContext({})

/**
 * Hook to manage external tab selection.
 * @param {string} [defaultSelected] - A tab `[id]` attribute value.
 * @return {[unknown, ((value: unknown) => void)]}
 */
export const useTabSelector = (defaultSelected) => {
  const [selected, setSelected] = useState(defaultSelected)
  return [selected, setSelected]
}

/**
 * Bronson Tabs component.
 *
 * @param {[TabsHeader, TabsContent]} [children] - The {@link TabsHeader} and {@link TabsContent} must be passed only.
 * @param {string} [className] - Additional class names for the container.
 * @param {string} [testId] - The testId string.
 * @param {string} [defaultSelected] - The `[id]` attribute of the initially activated tab.
 * @param {string} [selected] - The `[id]` attribute of the currently selected tab.
 * @param {function} [onChangeSelected] - Handler to call on tab selection change.
 *                                        Gets the `[id]` attribute of the currently selected tab as argument.
 * @param {object} [otherProps] - Other props to pass on the Tabs element.
 * @return {JSX.Element} - The Tabs element.
 * @constructor
 */
export function Tabs({ defaultSelected, selected, onChangeSelected, className, testId, children, ...otherProps }) {
  /**
   * To be downward compatible we still use this internal state when `defaultSelected` is available.
   */
  const [currSelected, setCurrSelected] = useState(defaultSelected)

  /**
   * Change the `isSelected()` and `setSelected()` functions depending on the `defaultSelected` state.
   * This is due the fact that either `defaultSelected` or `['selected, 'onChangeSelected']` can be used.
   * @type {{setSelected: (function(*=): void), isSelected: (function(*): boolean), selected: string}}
   */
  const contextValue = useMemo(() => {
    return defaultSelected
      ? {
          defaultSelected,
          selected: currSelected,
          // query whether the tab id is selected
          isSelected: (tabId) => currSelected === tabId,
          // callback function to set the selected tab
          setSelected: (tabId) => setCurrSelected(tabId),
        }
      : {
          defaultSelected: selected,
          selected,
          // query whether the tab id is selected
          isSelected: (tabId) => selected === tabId,
          // callback function to set the selected tab
          setSelected: (tabId) => onChangeSelected(tabId),
        }
  }, [currSelected, defaultSelected, onChangeSelected, selected])

  const classNameList = classNames('c-tabs', 'js-tabs', className).trim()

  return (
    <div {...otherProps} className={classNameList} data-testid={testId}>
      <TabsContext.Provider value={contextValue}>
        {/* Use `<Tabs.Header>` and `<Tabs.Content>` components. */}
        {children}
      </TabsContext.Provider>
    </div>
  )
}

/**
 * Either use `defaultSelected` or `['selected, 'onChangeSelected']`.
 */
Tabs.propTypes = {
  defaultSelected: PropTypes.string, // set the initial selected tab (=default tab)
  selected: PropTypes.string, // change the selected tab from outside via this property
  onChangeSelected: PropTypes.func, // useTabSelector hook provides default logic to update current tab and handle selected tab state
  className: PropTypes.string,
  testId: PropTypes.string, // Added for data-testid attribute.
  children: PropTypes.node, // Bronson template: 'tabs-item'. Use 'Tabs.Header' and 'Tabs.Content' components.
}

/**
 * Bronson TabsHeader component.
 *
 * @param {TabsHeaderItem[]} [children] - Collection of {@link TabsHeaderItem} children.
 * @param {string} [className] - Additional class names for the container.
 * @param {string} [testId] - The testId string.
 * @param {string} [icons] - Use if you plan to use icons for the tabs.
 * @param {string} [iconsOnly] - Hide the text inside tabs and only show icons.
 * @param {string} [stacked] - Stack the icon on-top of the tab text.
 * @param {object} [otherProps] - Additional properties on the element.
 * @return {JSX.Element} - The TabsHeader element.
 * @constructor
 */
function TabsHeader({ className, testId, children, icons, iconsOnly, stacked, ...otherProps }) {
  const initialResizeThreshold = 100
  const { defaultSelected, selected } = React.useContext(TabsContext)
  const [flickityRef, setFlickityRef] = useState(null)

  /**
   * Set the initialIndex depending on the current select tab.
   * If the default and selected tab mismatch we assume the user has navigated themselves.
   * @type {number|null}
   */
  const initialIndex =
    defaultSelected === selected ? children.findIndex((c) => c.props.tabId === defaultSelected) : null

  /**
   * Handle BasicSlider side-effects.
   */
  useEffect(() => {
    const instance = flickityRef
    /**
     * If we can obtain a {@link flickityRef} select the current index to keep the active tab visible.
     * @see https://flickity.metafizzy.co/api.html#select
     */
    if (instance) {
      const resizeFn = instance.resize.bind(instance)

      instance.resize = () => {
        instance.element.classList.remove('flickity-resize')
        resizeFn()
        requestAnimationFrame(() => instance.element.classList.add('flickity-resize'))
      }

      /**
       * Check if we have an initial value and slide to this tab.
       */
      if (initialIndex) {
        /**
         * We select the cell here as we deal with `groupCell`.
         * @see https://flickity.metafizzy.co/api.html#selectcell
         */
        instance.selectCell(initialIndex, false, true)
      }

      const onReady = () => {
        debounce(instance.resize, initialResizeThreshold)()
        /**
         * Fixes nasty layouting bug for some journeys.
         * The {@link initialResizeThreshold} must be increased for this to work.
         * @see https://jira.platform.vwfs.io/browse/BRON-7534
         */
        const timeout = setTimeout(() => {
          resizeFn()
        }, initialResizeThreshold + 50)
        return () => {
          timeout && clearTimeout(timeout)
        }
      }
      instance.on('select', toggleSliderControls)
      instance.on('ready', onReady)
      return () => {
        instance.off('select', toggleSliderControls)
        instance.off('ready', onReady)
      }
    }
    return () => {}
  }, [flickityRef, initialIndex, toggleSliderControls])

  const toggleSliderControls = useCallback(() => {
    if (flickityRef) {
      const size = flickityRef.size.width
      const { slideableWidth } = flickityRef
      const slideable = slideableWidth > size

      const pageDotsHTMLCollection = flickityRef.element.getElementsByClassName('flickity-page-dots')

      /**
       * This fixes a bug where Flickity is unable to reassign the draggable behavior when resizing from a big to small
       * viewport when `groupCells` or `contain` options are set.
       * The fix only kicks in when the `draggable` option {@link https://flickity.metafizzy.co/options.html#draggable} in Flickity was set.
       * @see https://github.com/metafizzy/flickity/issues/960
       */
      if (flickityRef.options.draggable) {
        flickityRef.isDraggable = slideable
        flickityRef.element.classList.toggle('is-draggable', slideable)
        flickityRef.element.classList.toggle('is-slideable', slideable)
      }

      Array.from(pageDotsHTMLCollection).forEach((pageDots) => {
        pageDots.classList.toggle('is-slideable', slideable)
      })
    }
  }, [flickityRef])

  // generated
  const tabsClassNameList = classNames(
    'c-tabs__nav',
    'js-tabs__nav',
    {
      'c-tabs__nav--icons': icons,
      'c-tabs__nav--icons-stacked': stacked,
      'c-tabs__nav--icons-only': iconsOnly,
    },
    className
  ).trim()

  const flickityOptions = {
    cellSelector: '.js-tabs__nav-item',
    contain: true, // Prevent excess scroll at beginning or end of the slider.
    pageDots: false,
    cellAlign: 'left',
    groupCells: true,
    freeScroll: true,
    prevNextButtons: true,
    initialIndex,
  }

  return (
    <nav {...otherProps} data-testid={testId} role="tablist">
      <BasicSlider
        elementType="div"
        className={tabsClassNameList}
        flickityRef={(c) => setFlickityRef(c)}
        options={flickityOptions}
      >
        {children}
      </BasicSlider>
    </nav>
  )
}

TabsHeader.propTypes = {
  className: PropTypes.string,
  testId: PropTypes.string, // Added for data-testid attribute.
  defaultSelected: PropTypes.string, // prop drilling to avoid rerender via context.
  children: PropTypes.node,
  // Convenience props
  icons: PropTypes.bool, // Bronson template: 'tabs-nav-modifiers' → `.c-tabs__nav--icons`.
  iconsOnly: PropTypes.bool, // Bronson template: 'tabs-nav-modifiers' → `.c-tabs__nav--icons-only`.
  stacked: PropTypes.bool, // Bronson template: 'tabs-nav-modifiers' → `.c-tabs__nav--icons-stacked`.
}

TabsHeader.displayName = 'Tabs.Header'
Tabs.Header = TabsHeader

/**
 * Bronson TabsHeaderItem component.
 *
 * @param {string|React.node} [children]
 * @param {string} [tabId] - The value for the tab `[id]` attribute.
 * @param {string} [className] - Additional class names for the container.
 * @param {string} [testId] - The testId string.
 * @param {string} [icon] - The icon identifier, e.g. `semantic-clock`.
 * @param {string} [disabled] - Disable the tab.
 * @param {object} [otherProps] - Additional properties on the element.
 * @return {JSX.Element} - The TabsHeaderItem element.
 */
function TabsHeaderItem({ tabId, className, icon, disabled, testId, children, ...otherProps }) {
  const context = React.useContext(TabsContext)

  const buttonClassNameList = classNames(
    'c-tabs__nav-link',
    'js-tabs__nav-link',
    {
      'is-disabled ': disabled,
      'is-active ': !disabled && context.isSelected(tabId),
    },
    className
  ).trim()

  const iClassNameList = classNames('c-tabs__icon', 'c-icon', `c-icon--[${icon}]`).trim()

  // generated
  function renderIfTabsItemIcon() {
    if (icon) {
      return <i className={iClassNameList} aria-hidden="true" role="img" />
    }
    return null
  }

  // generated main result
  return (
    <div
      {...otherProps}
      className="c-tabs__nav-item  js-tabs__nav-item"
      data-testid={testId}
      role="tab"
      aria-selected={!disabled && context.isSelected(tabId)}
    >
      <button
        className={buttonClassNameList}
        aria-expanded={!disabled && context.isSelected(tabId)}
        disabled={disabled}
        tabIndex={!disabled && context.isSelected(tabId) ? '-1' : null}
        onClick={() => !disabled && context.setSelected(tabId)}
        type="button"
      >
        {renderIfTabsItemIcon()}
        <span className="c-tabs__text">{children}</span>
      </button>
    </div>
  )
}

TabsHeaderItem.propTypes = {
  tabId: PropTypes.string.isRequired,
  className: PropTypes.string,
  icon: PropTypes.string, // Bronson template: 'tabs-item-icon'.
  testId: PropTypes.string, // Added for data-testid attribute.
  children: PropTypes.node, // Bronson template: 'tabs-item-text'.
}

TabsHeaderItem.displayName = 'Tab.HeaderItem'
Tabs.HeaderItem = TabsHeaderItem

/**
 * Bronson TabsContent component.
 *
 * @param {TabsContentItem[]} [children] - Collection of tab content children.
 * @param {string} [testId] - The testId string.
 * @param {object} [otherProps] - Additional properties on the element.
 * @return {JSX.Element} - The TabsContent element.
 * @constructor
 */
function TabsContent({ testId, children, ...otherProps }) {
  return (
    <div data-testid={testId} {...otherProps} className="c-tabs__content-wrapper">
      {children}
    </div>
  )
}

TabsContent.propTypes = {
  testId: PropTypes.string, // Added for data-testid attribute.
  children: PropTypes.node,
}

TabsContent.displayName = 'Tabs.Content'
Tabs.Content = TabsContent

/**
 * Bronson TabsContentItem component (nested).
 *
 * @param {string|React.node} [children]
 * @param {string} [tabId] - A reference to the {@link TabsHeaderItem.tabId} of the tab that controls this component.
 * @param {string} [className] - Additional class names for the container.
 * @param {string} [testId] - The testId string.
 * @param {object} [otherProps] - Additional properties on the element.
 * @return {JSX.Element} - The TabsContentItem element.
 * @constructor
 */
function TabsContentItem({ tabId, className, testId, children, ...otherProps }) {
  const context = React.useContext(TabsContext)

  const classNameList = classNames('c-tabs__content', 'js-tabs__content', 'js-is-ready', className).trim()

  return (
    <div
      {...otherProps}
      className={classNameList}
      aria-hidden={context.isSelected(tabId) ? 'false' : 'true'}
      data-testid={testId}
      role="tabpanel"
    >
      {children}
    </div>
  )
}

TabsContentItem.propTypes = {
  tabId: PropTypes.string.isRequired,
  className: PropTypes.string,
  testId: PropTypes.string, // Added for data-testid attribute.
}

TabsContentItem.displayName = 'Tabs.ContentItem'
Tabs.ContentItem = TabsContentItem
